import { useCallback, useEffect, useRef, useState } from 'react'

import { useEventStream } from './useEventStream'

/**
 * Данные, обновляемые событиями.
 *
 * Сводит воедино три вещи, которые иначе пришлось бы повторять
 * на каждом экране: первичная загрузка, точечное обновление
 * по событию и опрос как запасной путь.
 *
 * Событие не приносит данные — оно сообщает, что данные изменились.
 * Перезагружаем только затронутое: присылать полный список при каждом
 * изменении значит терять смысл реалтайма.
 */

const POLL_INTERVAL_MS = 15_000

export function useLiveData({ load, eventTypes, enabled = true, pollMs = POLL_INTERVAL_MS }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [lastEventAt, setLastEventAt] = useState(null)
  const pollTimer = useRef(null)
  const loadRef = useRef(load)

  loadRef.current = load

  const refresh = useCallback(async () => {
    setError('')
    try {
      setData(await loadRef.current())
    } catch (e) {
      setError(e?.response?.data?.message || 'Не удалось загрузить данные')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (enabled) refresh()
  }, [enabled, refresh])

  const onEvent = useCallback(
    (event) => {
      if (eventTypes && !eventTypes.includes(event.type)) return
      setLastEventAt(Date.now())
      refresh()
    },
    [eventTypes, refresh]
  )

  /**
   * Опрос включается только когда поток не работает.
   *
   * Держать оба одновременно значит удваивать нагрузку ради того,
   * что уже приходит событиями.
   */
  const startPolling = useCallback(() => {
    clearInterval(pollTimer.current)
    pollTimer.current = setInterval(refresh, pollMs)
  }, [refresh, pollMs])

  const { status, isLive } = useEventStream(onEvent, {
    enabled,
    onFallback: startPolling,
  })

  useEffect(() => {
    if (isLive) clearInterval(pollTimer.current)
    return () => clearInterval(pollTimer.current)
  }, [isLive])

  return { data, loading, error, refresh, isLive, status, lastEventAt }
}
